import React, { useEffect, useState } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
} from "react-icons/fa";
import {
  getData,
  postData,
  putData,
  deleteData,
} from "../../../services/FetchNodeservices";

export default function Designation() {
  const [designations, setDesignations] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const showToast = (msg, type = "success") => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast({ show: false }), 2500);
  };

  /* ================= FETCH ================= */
  const fetchDesignations = async () => {
    const res = await getData("designation/fetch");
    if (res?.status) setDesignations(res.data);
  };

  const fetchUsers = async () => {
    const res = await getData("designation/users");
    if (res?.status) setUsers(res.data);
  };

  useEffect(() => {
    fetchDesignations();
    fetchUsers();
  }, []);

  const filtered = designations.filter((d) =>
    d.designation_name.toLowerCase().includes(search.toLowerCase())
  );

  /* ================= MODALS ================= */
  const openAddModal = () => {
    setFormData({ designation_name: "", user_ids: [] });
    setModalOpen(true);
  };

  const openEditModal = (d) => {
    setFormData({
      designation_id: d.designation_id,
      designation_name: d.designation_name,
      user_ids: d.user_details.map((u) => u.id),
    });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const openDeleteConfirm = (id) => {
    setDeleteId(id);
    setDeleteModal(true);
  };

  const closeDelete = () => setDeleteModal(false);

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!formData.designation_name || formData.user_ids.length === 0) {
      showToast("Please enter name and assign users!", "error");
      return;
    }

    const payload = {
      designation_name: formData.designation_name,
      user_ids: formData.user_ids,
    };

    let res;
    if (formData.designation_id)
      res = await putData(
        `designation/update/${formData.designation_id}`,
        payload
      );
    else res = await postData("designation/add", payload);

    if (res?.status) {
      fetchDesignations();
      closeModal();
      showToast(res.message);
    } else {
      showToast(res?.message || "Operation failed", "error");
    }
  };

  const deleteDesignation = async () => {
    const res = await deleteData(`designation/delete/${deleteId}`);
    if (res?.status) {
      fetchDesignations();
      closeDelete();
      showToast("Designation deleted");
    } else showToast("Delete failed", "error");
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-100 pt-20 px-4 md:ml-72 md:px-6">
      {/* Toast */}
      {toast.show && (
        <div
          className={`fixed top-5 right-5 px-4 py-2 rounded-lg text-white shadow-xl z-[9999]
          ${toast.type === "success" ? "bg-green-600" : "bg-red-600"}`}
        >
          {toast.message}
        </div>
      )}

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h2 className="text-lg md:text-xl font-bold text-gray-700">
          Designation Management
        </h2>

        <button
          onClick={openAddModal}
          className="
            inline-flex items-center gap-2
            px-4 py-2 bg-blue-600 text-white text-sm
            rounded-lg shadow hover:bg-blue-700
            w-fit
          "
        >
          <FaPlus /> Add Designation
        </button>
      </div>

      {/* SEARCH */}
      <div className="relative w-full sm:w-64 mb-4">
        <FaSearch className="absolute top-3 left-3 text-gray-400 text-sm" />
        <input
          type="text"
          placeholder="Search designation..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 pr-3 py-2 border rounded-lg w-full bg-white shadow-sm text-sm outline-none"
        />
      </div>

      {/* TABLE */}
      <div className="bg-white shadow-sm rounded-2xl border border-slate-200/80 overflow-x-auto">
        <table className="min-w-[700px] w-full text-sm">
          <thead>
            <tr className="bg-slate-50/75 border-b border-slate-250 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
              <th className="px-6 py-3.5 text-center w-16">ID</th>
              <th className="px-6 py-3.5 text-left">Designation</th>
              <th className="px-6 py-3.5 text-left">Assigned Users</th>
              <th className="px-6 py-3.5 text-center w-32">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center p-6 text-gray-500">
                  No designations found
                </td>
              </tr>
            ) : (
              filtered.map((d) => (
                <tr key={d.designation_id} className="hover:bg-slate-50/50 transition-colors duration-150">
                  <td className="px-6 py-4 text-center text-slate-500 whitespace-nowrap">
                    {d.designation_id}
                  </td>
                  <td className="px-6 py-4 font-semibold text-slate-800 whitespace-nowrap">
                    {d.designation_name}
                  </td>
                  <td className="px-6 py-4 text-slate-600 whitespace-nowrap max-w-md truncate">
                    {d.user_names?.join(", ") || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex justify-center gap-2.5">
                      <button
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200"
                        onClick={() => openEditModal(d)}
                        title="Edit Designation"
                      >
                        <FaEdit className="text-sm" />
                      </button>
                      <button
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all duration-200"
                        onClick={() => openDeleteConfirm(d.designation_id)}
                        title="Delete Designation"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ADD / EDIT MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm flex justify-center items-center p-4 z-50">
          <div className="bg-white w-full max-w-lg p-6 rounded-2xl shadow-xl border border-slate-100">
            <h2 className="text-lg font-bold text-slate-800 mb-4">
              {formData.designation_id
                ? "Edit Designation"
                : "Add Designation"}
            </h2>

            <div className="space-y-4 text-sm">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  Designation Name
                </label>
                <input
                  className="border border-slate-200 rounded-xl px-3 py-2 w-full focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                  value={formData.designation_name || ""}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      designation_name: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  Assign Users
                </label>
                <div className="border border-slate-200 rounded-xl px-3 py-2 w-full max-h-48 overflow-y-auto bg-slate-50/50 shadow-inner space-y-1">
                  {users.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-2.5 py-1.5 px-2 hover:bg-white rounded-lg cursor-pointer transition-colors text-slate-700 font-medium"
                    >
                      <input
                        type="checkbox"
                        className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 border-slate-300"
                        checked={formData.user_ids?.includes(u.id)}
                        onChange={() =>
                          setFormData((p) => ({
                            ...p,
                            user_ids: p.user_ids.includes(u.id)
                              ? p.user_ids.filter((x) => x !== u.id)
                              : [...p.user_ids, u.id],
                          }))
                        }
                      />
                      {u.full_name}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 gap-2.5">
              <button
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-700 font-medium transition-colors"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors shadow-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center p-4 z-50">
          <div className="bg-white max-w-sm w-full p-6 rounded-xl shadow-lg">
            <h3 className="font-bold mb-4">Confirm Delete</h3>
            <p className="text-sm text-gray-600 mb-4">
              Do you want to delete this designation?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 border rounded"
                onClick={closeDelete}
              >
                Cancel
              </button>
              <button
                onClick={deleteDesignation}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
